import React, { useState, useCallback, useMemo } from 'react';
import { InputDropdown } from '@mercell/input-dropdown-react';
import FieldError from '@mercell/field-error-react';
import { FieldRenderProps } from 'react-final-form';
import { normalizeNumberString, formatNumberForLocale, normalizeFieldError } from './numericFieldUtils';

interface InputDropdownValue {
    inputValue: string;
    selectedOptions: string;
}

interface Option {
    label: string;
    value: string;
}

interface NumericInputDropdownProps {
    locale: string;
    options: Option[];
    dropdownSide?: 'left' | 'right';
    dropdownMaxWidth?: string;
    dropdownProps?: Record<string, unknown>;
    disabled?: boolean;
    ariaRequired?: boolean;
    fieldLabel?: (name: string, isRequired?: boolean) => React.ReactNode;
    fieldWrapperProperties?: Record<string, unknown>;
    showErrorsOnInitialRender?: boolean;
}

export const createNumericInputDropdownRender = ({
    locale,
    options,
    dropdownSide = 'left',
    dropdownMaxWidth = '50%',
    dropdownProps = {},
    disabled = false,
    ariaRequired = false,
    fieldLabel,
    fieldWrapperProperties = {},
}: NumericInputDropdownProps) =>
    // eslint-disable-next-line react/display-name
    ({ input, meta }: FieldRenderProps<InputDropdownValue>) => {
        const [isFocused, setIsFocused] = useState(false);

        const rawValue: InputDropdownValue = useMemo(
            () =>
                input.value || {
                    inputValue: '',
                    selectedOptions: '',
                },
            [input.value]
        );

        // Format display value based on focus state
        const displayInputValue = isFocused
            ? rawValue.inputValue
            : formatNumberForLocale(rawValue.inputValue, locale);

        const fieldError = normalizeFieldError(meta.error) || normalizeFieldError(meta.submitError);
        const showErrors =
            ((meta.touched || meta.submitFailed) && fieldError && !meta.active) ||
            (meta.submitError && meta.submitFailed);

        const handleFocus = useCallback(() => {
            setIsFocused(true);
        }, []);

        const handleBlur = useCallback(() => {
            setIsFocused(false);
            // Normalize the input value on blur
            const normalizedValue: InputDropdownValue = {
                ...rawValue,
                inputValue: normalizeNumberString(rawValue.inputValue),
            };
            input.onChange(normalizedValue);
            input.onBlur();
        }, [rawValue, input]);

        const handleChange = useCallback(
            (inputValue: string, selectedOption: unknown) => {
                const option = selectedOption as { value: string } | null;
                // Normalize the input value to ensure we always store raw numeric value
                // This handles the case where the dropdown changes while showing formatted value
                const normalizedInputValue = isFocused
                    ? inputValue
                    : normalizeNumberString(inputValue);
                input.onChange({
                    inputValue: normalizedInputValue,
                    selectedOptions: option?.value || '',
                });
            },
            [input, isFocused]
        );

        const selectedOption = options.find(
            (opt) => opt.value === rawValue.selectedOptions
        );

        return (
            <div {...fieldWrapperProperties} data-field={input.name}>
                {showErrors && <FieldError>{fieldError}</FieldError>}
                {fieldLabel && fieldLabel(input.name, false)}
                <InputDropdown
                    inputValue={displayInputValue}
                    selectedOptions={selectedOption}
                    onChangeInputDropdown={handleChange}
                    dropdownOptions={options}
                    dropdownSide={dropdownSide}
                    dropdownMaxWidth={dropdownMaxWidth}
                    dropdownProps={dropdownProps}
                    disabled={disabled}
                    invalid={showErrors}
                    inputProps={{
                        name: input.name,
                        id: input.name,
                        'aria-required': ariaRequired,
                        onFocus: handleFocus,
                        onBlur: handleBlur,
                    }}
                />
            </div>
        );
    };
